// Lib
import React from 'react';
import { DetailedCellError, ErrorType } from 'hyperformula';
import { useSelector } from 'react-redux';

// Utils
import { isSelectingSingleSelectionAndCell } from '../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import { getFirstSelection } from '../../bridge/utils/selectionUtils/tableSelectionAccessors';

// Components
import Icon from '../../../../components/icons/Icon';
import TooltipSource from '../../../../components/tooltips/TooltipSource';

// Hooks
import { useActiveTableSelector } from '../../bridge/store/hotTableStoreHooks';
import { selectActiveTableCellSelectionForElement } from '../../bridge/store/selectors/activeHotTableCellSelectionSelectors';

// Constants
import { MilanoteApplicationMode } from '../../../../../common/platform/platformTypes';

// Selectors
import { getMilanoteApplicationModeSelector } from '../../../../platform/platformSelector';

// Styles
import './CellFormulaErrorRenderer.scss';
import { useGroupTranslator } from '../../../../components/i18n/GroupTranslationContext';

interface Props {
    cellError: DetailedCellError;
    elementId: string;
    row: number;
    col: number;
}

const removeTrailingFullStop = (message: string): string => (message.endsWith('.') ? message.slice(0, -1) : message);

/**
 * Converts HyperFormula errors into the most helpful localised tooltip text.
 */
const getTooltipText = (
    { message, type, value }: DetailedCellError,
    translate: ReturnType<typeof useGroupTranslator<'elements', 'elementTypes.table'>>,
): string => {
    // Parsing errors from HyperFormula tend to be long-winded and unreadable. Replace them with a simpler error.
    if (message.startsWith('Parsing error.')) return translate('formula.errorParsing');
    if (message) return removeTrailingFullStop(message);

    if (type === ErrorType.DIV_BY_ZERO) return translate('formula.errorDivByZero');
    if (type === ErrorType.CYCLE) return translate('formula.errorCircularReference');

    return value;
};

const CellFormulaErrorRenderer: React.FC<Props> = (props) => {
    const { cellError, elementId, row, col } = props;

    const translate = useGroupTranslator<'elements', 'elementTypes.table'>();

    const applicationMode = useSelector(getMilanoteApplicationModeSelector);
    const selectedCells = useActiveTableSelector(selectActiveTableCellSelectionForElement(elementId));
    const selectedCell = isSelectingSingleSelectionAndCell(selectedCells)
        ? getFirstSelection(selectedCells)
        : undefined;
    const isSelectedCell = selectedCell?.[0] === row && selectedCell?.[1] === col;

    const isMobile = applicationMode === MilanoteApplicationMode.mobile;

    // On mobile, show error tooltip immediately on cell selection
    const shouldTriggerTooltip = isMobile && isSelectedCell;

    // This tooltip mounts after selection's pointer-down but before its click; the default click listener would dismiss it immediately.
    const hideTooltipOnEvent = shouldTriggerTooltip ? 'pointerdown' : undefined;

    return (
        <div className="CellFormulaErrorRenderer">
            <TooltipSource
                enabled
                className="cell-formula-error-tooltip-source"
                tooltipText={
                    <div className="tooltip-cell-formula-error-content">
                        <Icon name="error-alert-round" />
                        {getTooltipText(cellError, translate)}
                    </div>
                }
                pollPosition
                distance={10}
                delay={shouldTriggerTooltip ? 0 : 200}
                triggerOnMouseEnter={!isMobile}
                shouldTrigger={shouldTriggerTooltip}
                hideOnEvent={hideTooltipOnEvent}
            >
                <Icon name="error-alert-round"></Icon>
            </TooltipSource>
        </div>
    );
};

export default CellFormulaErrorRenderer;
